// src/views/DashboardView.vue
<template>
  <div class="dashboard-container">
    
    <div class="dashboard-content">
      <h4>📊 Statistiques générales</h4>
      
      <div v-if="loading" class="loading-container">
        <div class="loader"></div>
        <p>Chargement des statistiques...</p>
      </div>
      
      <div v-else>
        <!-- Cartes de statistiques -->
        <div class="dashboard-cards">
          <div class="dashboard-card card-blue animate-in" >
            <div class="card-content">
              <div class="card-info">
                <h3>{{ stats.totalPersonnel || 0 }}</h3>
                <p>Personnels</p>
              </div>
              <div class="card-icon-wrapper">
                <span class="material-icons card-icon">people</span>
              </div>
            </div>
            <div class="card-footer" @click="goTo('personnel')">
              <span>Voir détails</span>
              <span class="material-icons">arrow_forward</span>
            </div>
          </div>
          
          <div class="dashboard-card card-green animate-in" style="animation-delay: 0.1s">
            <div class="card-content">
              <div class="card-info">
                <h3>{{ stats.totalPointagesToday || 0 }}</h3>
                <p>Pointages (aujourd'hui)</p>
              </div>
              <div class="card-icon-wrapper">
                <span class="material-icons card-icon">fact_check</span>
              </div>
            </div>
            <div class="card-footer" @click="goTo('pointage/calendrier')">
              <span>Voir détails</span>
              <span class="material-icons">arrow_forward</span>
            </div>
          </div>
          
          <div class="dashboard-card card-orange animate-in" style="animation-delay: 0.2s">
            <div class="card-content">
              <div class="card-info">
                <h3>{{ stats.totalMouvementsToday || 0 }}</h3>
                <p>Mouvements (aujourd'hui)</p>
              </div>
              <div class="card-icon-wrapper">
                <span class="material-icons card-icon">directions_walk</span>
              </div>
            </div>
            <div class="card-footer">
              <span>Voir détails</span>
              <span class="material-icons">arrow_forward</span>
            </div>
          </div>
        </div>
        
        <!-- Tableau et graphiques -->
        <div class="dashboard-flex">
          <!-- Tableau des pointages -->
          <div class="dashboard-table">
            <div class="table-card">
              <div class="table-header">
                <h5>Pointages du jour</h5>
              </div>
              
              <PointageTable 
                :data="filteredData" 
                :loading="loading"
                :current-page="currentPage"
                :page-size="pageSize"
                :total-items="totalItems"
                @page-changed="handlePageChange"
                @page-size-changed="handlePageSizeChange"
              />
            </div>
          </div>
          
          <!-- Graphiques -->
          <div class="dashboard-graphs">
            <!-- Graphique par sexe -->
            <div class="graph-card">
              <h5>Répartition par sexe</h5>
              <SexeChart 
                v-if="stats.pointagesParSexe && stats.pointagesParSexe.length" 
                :data="stats.pointagesParSexe" 
              />
              <div v-else class="no-data">Aucune donnée disponible</div>
            </div>
            
            <!-- Graphique par direction -->
            <div class="graph-card">
              <h5>Statistiques par direction</h5>
              <DirectionChart 
                v-if="pointagesData.length" 
                :data="pointagesData" 
              />
              <div v-else class="no-data">Aucune donnée disponible</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/services/api'

// Composants
import PointageTable from '@/components/tables/PointageTable.vue'
import SexeChart from '@/components/charts/SexeChart.vue'
import DirectionChart from '@/components/charts/DirectionChart.vue'

const router = useRouter()

// État
const stats = ref({})
const pointagesData = ref([])
const loading = ref(true)
const searchQuery = ref('')
const currentPage = ref(1)
const pageSize = ref(10)
const totalItems = ref(0)

// Méthodes
const fetchDashboardData = async () => {
  loading.value = true
  try {
    const response = await api.getDashboardStats()
    stats.value = response.data
    
    // Charger les pointages
    const pointagesResponse = await api.getPointagesToday()
    
    // S'assurer que pointagesData est un tableau
    if (pointagesResponse.data && pointagesResponse.data.pointagesToday) {
      // Si la nouvelle API retourne un objet avec une propriété pointages
      pointagesData.value = pointagesResponse.data.pointagesToday
    } else {
      // Fallback si aucun des deux formats n'est correct
      console.warn('Format de données inattendu pour les pointages')
      pointagesData.value = []
    }
  } catch (error) {
    console.error('Erreur lors du chargement des données:', error)
    pointagesData.value = []
  } finally {
    loading.value = false
  }
}

const goTo = (route) => {
  router.push(`/${route}`)
}

// Données filtrées
const filteredData = computed(() => {
  // Vérifier si pointagesData.value est un tableau
  if (!Array.isArray(pointagesData.value)) {
    console.warn('pointagesData.value n\'est pas un tableau:', pointagesData.value)
    return []
  }

  let result = pointagesData.value
  
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter(p => 
      (p.personnel && p.personnel.toLowerCase().includes(query)) || 
      (p.direction && p.direction.toLowerCase().includes(query))
    )
  }
  
  return result
})

// Méthode de pagination
const handlePageChange = (page) => {
  currentPage.value = page
}

// Méthode de changement de taille de page
const handlePageSizeChange = (size) => {
  pageSize.value = size
  currentPage.value = 1 // Revenir à la première page lors du changement de taille
}

// Mettre à jour le nombre total d'items lors du filtrage
watch(filteredData, (newFilteredData) => {
  totalItems.value = newFilteredData.length
})

// Cycle de vie
onMounted(() => {
  fetchDashboardData()
  
  // Initialiser le nombre total d'items
  totalItems.value = pointagesData.value.length
  
  // Actualisation toutes les 5 minutes
  const interval = setInterval(() => {
    fetchDashboardData()
  }, 5 * 60 * 1000)
  
  // Nettoyer l'intervalle lors de la destruction du composant
  return () => clearInterval(interval)
})
</script>

<style scoped>
/* Import des variables de couleurs OpSolver */
@import '@/assets/css/variables.css';

.dashboard-container {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  width: 100%;
  background-color: #f9fafb;
  position: relative;
}

.dashboard-content {
  padding: 24px 32px;
  flex-grow: 1;
  width: 100%;
  box-sizing: border-box;
  max-width: 1600px;
  margin: 0 auto;
}

h4 {
  margin-top: 0;
  margin-bottom: 20px;
  font-weight: 600;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.loader {
  border: 4px solid #ccc;
  border-top: 4px solid #1976d2;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Cartes et graphiques */
.dashboard-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 30px;
  transition: all 0.3s ease;
}

.dashboard-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: transform 0.3s, box-shadow 0.3s;
  display: flex;
  flex-direction: column;
}

.dashboard-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
}

.card-content {
  padding: 24px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.card-info {
  flex: 1;
}

.card-icon-wrapper {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-icon {
  font-size: 28px;
}

.dashboard-card h3 {
  font-size: 30px;
  font-weight: 600;
  margin: 0;
  margin-bottom: 8px;
  line-height: 1;
}

.dashboard-card p {
  margin: 0;
  color: var(--turquoise-very-dark);
  font-size: 14px;
  font-weight: 500;
}

.card-footer {
  padding: 14px 24px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: #666;
  transition: background-color 0.2s;
}

.dashboard-card:hover .card-footer {
  background-color: #f9fafb;
}

.card-blue {
  border-top: 4px solid var(--turquoise-primary);
}

.card-blue .card-icon-wrapper {
  background-color: rgba(32, 211, 208, 0.1);
}

.card-blue .card-icon {
  color: var(--turquoise-primary);
}

.card-green {
  border-top: 4px solid var(--turquoise-dark);
}

.card-green .card-icon-wrapper {
  background-color: rgba(25, 171, 169, 0.1);
}

.card-green .card-icon {
  color: var(--turquoise-dark);
}

.card-orange {
  border-top: 4px solid var(--turquoise-deep);
}

.card-orange .card-icon-wrapper {
  background-color: rgba(13, 90, 89, 0.1);
}

.card-orange .card-icon {
  color: var(--turquoise-deep);
}

/* Tableau et graphiques */
.dashboard-flex {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.dashboard-table {
  flex: 1 1 60%;
  min-width: 400px;
}

.dashboard-graphs {
  flex: 1 1 34%;
  min-width: 300px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.table-card, .graph-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  height: 100%;
  box-sizing: border-box;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.table-header h5 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--turquoise-very-dark);
}

.table-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.search-input {
  padding: 10px 16px;
  border: 1px solid #eaeaea;
  border-radius: 8px;
  width: 240px;
  font-size: 14px;
  transition: border-color 0.2s;
  background-color: #f9fafb;
}

.search-input:focus {
  outline: none;
  border-color: var(--turquoise-primary);
  background-color: white;
}

.graph-card {
  display: flex;
  flex-direction: column;
}

.graph-card h5 {
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--turquoise-very-dark);
}

.no-data {
  padding: 40px;
  text-align: center;
  color: #777;
  font-style: italic;
  background-color: #f9fafb;
  border-radius: 8px;
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

@keyframes fadeUp {
  0% {
    opacity: 0;
    transform: translateY(30px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-in {
  opacity: 0;
  animation: fadeUp 0.8s ease-out forwards;
}

@media (max-width: 900px) {
  .dashboard-card {
    flex: 0 0 100%;
    min-width: 0;
  }

  .dashboard-table,
  .dashboard-graphs {
    width: 100%;
    min-width: unset;
  }
}
</style>